'use client'

import React from 'react'
import CalendarHeader from './CalendarHeader'
import CalendarGrid from './CalendarGrid'
import useCalendar from './useCalendar'
import Button from '@/components/Button'

/**
 * Calendar Component
 * Reusable calendar for selecting available consultation dates
 *
 * @param {Object} props - Component props
 * @param {Function} props.onDateSelect - Callback when date is selected (receives YYYY-MM-DD string)
 * @param {string} props.selectedDate - Currently selected date (YYYY-MM-DD) for controlled component
 * @param {string} props.className - Additional CSS classes
 *
 * @example
 * <Calendar
 *   onDateSelect={(date) => console.log(date)}
 *   selectedDate="2025-12-05"
 * />
 */
const Calendar = ({ onDateSelect, selectedDate: externalSelectedDate, className = '' }) => {
  const {
    availableDates,
    calendarDates,
    currentMonth,
    selectedDate: internalSelectedDate,
    loading,
    error,
    fetchAvailability,
    setSelectedDate
  } = useCalendar()

  // Use external selectedDate if provided (controlled), otherwise internal (uncontrolled)
  const selectedDate = externalSelectedDate !== undefined ? externalSelectedDate : internalSelectedDate

  // Handle date click
  const handleDateClick = (dateStr) => {
    setSelectedDate(dateStr)
    if (onDateSelect) {
      onDateSelect(dateStr)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary" />
        <span className="ml-3 text-gray-600">Loading available dates...</span>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 text-center ${className}`}>
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={fetchAvailability} variant="secondary">
          Retry
        </Button>
      </div>
    )
  }

  // Main calendar
  return (
    <div
      className={`w-full max-w-md mx-auto ${className}`}
      role="region"
      aria-label="Calendar date picker"
    >
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <CalendarHeader currentMonth={currentMonth} />
        <CalendarGrid
          dates={calendarDates}
          onDateClick={handleDateClick}
          selectedDate={selectedDate}
          availableDates={availableDates}
          currentMonth={currentMonth}
        />
      </div>
    </div>
  )
}

export default Calendar
